import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { IntroOnce } from './components/layout/IntroScreen';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { EnvelopeDetailPage } from './pages/dashboard/EnvelopeDetailPage';
import { EnvelopeNewPage } from './pages/dashboard/EnvelopeNewPage';
import { LibraryPage } from './pages/dashboard/LibraryPage';
import { TemplatesListPage } from './pages/dashboard/TemplatesListPage';
import { AuditLogPage } from './pages/settings/AuditLogPage';
import { NotFoundPage } from './pages/public/NotFoundPage';
import { VerifyPage } from './pages/public/VerifyPage';
import { SignPage } from './pages/sign/SignPage';
import { BrandingPage } from './pages/settings/BrandingPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminBillingPage } from './pages/admin/AdminBillingPage';
import { AdminIntegrationsPage } from './pages/admin/AdminIntegrationsPage';
import { AdminApprovalMatrixPage } from './pages/admin/AdminApprovalMatrixPage';
import { AdminFoldersPage } from './pages/admin/AdminFoldersPage';
import { AdminOrganizationPage } from './pages/admin/AdminOrganizationPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { BillingPage } from './pages/settings/BillingPage';
import { IntegrationsPage } from './pages/settings/IntegrationsPage';
import { NotificationsPage } from './pages/settings/NotificationsPage';
import { ProfilePage } from './pages/settings/ProfilePage';
import { SecurityPage } from './pages/settings/SecurityPage';
import { SignaturePage } from './pages/settings/SignaturePage';
import { TeamPage } from './pages/settings/TeamPage';
import { WebhooksPage } from './pages/settings/WebhooksPage';
import { useAuthStore } from './stores/auth.store';

function RequireRole({
  children,
  allowed,
}: {
  children: ReactNode;
  allowed: ReadonlyArray<'owner' | 'admin' | 'manager' | 'member' | 'viewer'>;
}) {
  const user = useAuthStore((s) => s.user);
  if (!user || !allowed.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export function App() {
  // Suite intro splash at the app root — plays immediately on every full page
  // load (e.g. "Go To ESD" from isosofts.com), independent of the SSO exchange,
  // exactly like Algebra/IsoSofts. Skipped on the public signing/verify routes,
  // since external signers are not IsoSofts users and shouldn't see the suite splash.
  const { pathname } = useLocation();
  const isPublicRoute = pathname.startsWith('/sign') || pathname.startsWith('/verify');

  return (
    <>
      {!isPublicRoute && <IntroOnce />}
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Public signing routes — external recipients are NOT IsoSofts users,
          so these must never be gated behind the IsoSofts SSO. */}
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/verify/:id" element={<VerifyPage />} />

      {/* Authenticated */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* The Documents list is now embedded in the Dashboard; keep the old
          paths as redirects so bookmarks/links still resolve. "/inbox"
          (Documents for sign) lands on the "Awaiting my signature" tab. */}
      <Route path="/inbox" element={<Navigate to="/dashboard?tab=pending_mine" replace />} />
      <Route path="/envelopes" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/envelopes/new"
        element={
          <ProtectedRoute>
            <EnvelopeNewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/envelopes/:uuid"
        element={
          <ProtectedRoute>
            <EnvelopeDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <TemplatesListPage />
          </ProtectedRoute>
        }
      />
      {/* Reports were merged into the Dashboard. */}
      <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      {/* The Document Register moved inside the Document Library. */}
      <Route path="/registry" element={<Navigate to="/library" replace />} />

      {/* Admin panel — owner/admin only (enforced inside AdminLayout) */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/organization"
        element={
          <ProtectedRoute>
            <AdminOrganizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute>
            <AdminBillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute>
            <AdminIntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute>
            <AdminAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/approval-matrix"
        element={
          <ProtectedRoute>
            <AdminApprovalMatrixPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/folders"
        element={
          <ProtectedRoute>
            <AdminFoldersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={<Navigate to="/settings/profile" replace />}
      />
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/security"
        element={
          <ProtectedRoute>
            <SecurityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/signature"
        element={
          <ProtectedRoute>
            <SignaturePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/team"
        element={
          <ProtectedRoute>
            <RequireRole allowed={['owner', 'admin']}>
              <TeamPage />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/branding"
        element={
          <ProtectedRoute>
            <BrandingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/audit"
        element={
          <ProtectedRoute>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/integrations"
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/webhooks"
        element={
          <ProtectedRoute>
            <WebhooksPage />
          </ProtectedRoute>
        }
      />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
